import React from 'react';
import noop from 'lodash/noop';
import styled from 'styled-components';
import { BusinessMemberStatus } from '../../types';
import { useTranslation } from '../../utils/useTranslation';
import { Skeleton } from '../feedback/Skeleton';
import { IconClose } from '../icon/IconClose';
import { Button } from '../input/Button';
import { IconButton } from '../input/IconButton';
import { Flex } from '../layout/Flex';
import { Avatar } from '../media/Avatar';
import { Typography } from '../typography/Typography';
import { BusinessMemberUI } from './types';

const Root = styled(Flex).withConfig({ displayName: 'Root' }).attrs({
    direction: 'row',
    width: '100%',
    alignItems: 'center',
    spacing: 2,
})`
    position: relative;

    &:not(:last-child)::after {
        content: '';
        position: absolute;
        display: block;
        left: 48px;
        bottom: 0;
        right: 0;
        border-bottom: 2px solid var(--divider);
    }
`;

interface MemberCardProps {
    member: BusinessMemberUI;
    onApprove?: (memberId: string) => void;
    onRemove?: (memberId: string) => void;
    isLastMember?: boolean;
}

export const MemberCard = (props: MemberCardProps) => {
    const t = useTranslation();
    const { member, onApprove = noop, onRemove = noop } = props;

    return (
        <Root>
            <Avatar src={member.avatarUrl} title={member.displayName} />
            <Flex
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                py={1}
            >
                <Flex>
                    <Typography variant="body">{member.displayName}</Typography>
                    <Typography variant="caption" color="secondary">
                        {member.login}
                    </Typography>
                </Flex>
                <Flex direction="row" alignItems="center" spacing={2}>
                    {member.status === BusinessMemberStatus.PENDING_APPROVAL && (
                        <Button color="primary" onClick={() => onApprove(member.memberId)}>
                            {t('Подтвердить')}
                        </Button>
                    )}
                    {!member.isCurrentUser && (
                        <IconButton>
                            <IconClose size={20} onClick={() => onRemove(member.memberId)} />
                        </IconButton>
                    )}
                </Flex>
            </Flex>
        </Root>
    );
};

export const MemberCardSkeleton = () => (
    <Root>
        <Skeleton variant="circular" minWidth={32} height={32} />
        <Flex
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            py={1}
        >
            <Flex>
                <Skeleton variant="text" width={150} height={20} />
                <Skeleton variant="text" width={120} height={14} />
            </Flex>
            <Flex direction="row" alignItems="center" spacing={2}>
                <Skeleton width="20px" height="20px" variant="circular" />
            </Flex>
        </Flex>
    </Root>
);
